import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ErrorBar,
} from "recharts";
import { TimePeriod } from "./TimePeriodSelector";
import { getDayXAxis, getXAxisTickFormatterForPeriod } from "./axis";
import type {
  BloodPressureResponse,
  BloodPressureCategory,
} from "@/types/tracker";
import { CHART_COLORS } from "./chart-theme";

// Blood pressure stroke colors — primary/secondary chart hues.
const BLOOD_PRESSURE_COLORS = {
  systolic: "oklch(var(--chart-1))",
  diastolic: "oklch(var(--chart-2))",
};

// Blood pressure categories — semantic + chart token gradient mapped to severity.
// (Web-only palette. Flutter maintains its own iOS-style colors.)
const BLOOD_PRESSURE_CATEGORIES: BloodPressureCategory[] = [
  {
    label: "Low",
    systolicMax: 89,
    diastolicMax: 59,
    color: "oklch(var(--destructive))",
  },
  {
    label: "Normal",
    systolicMin: 90,
    systolicMax: 120,
    diastolicMin: 60,
    diastolicMax: 80,
    color: "oklch(var(--success))",
  },
  {
    label: "Elevated",
    systolicMin: 121,
    systolicMax: 129,
    diastolicMin: 60,
    diastolicMax: 80,
    color: "oklch(var(--chart-4))",
  },
  {
    label: "Hyp Stage 1",
    systolicMin: 130,
    systolicMax: 139,
    diastolicMin: 81,
    diastolicMax: 89,
    color: "oklch(var(--chart-3))",
  },
  {
    label: "Hyp Stage 2",
    systolicMin: 140,
    systolicMax: 179,
    diastolicMin: 90,
    diastolicMax: 119,
    color: "oklch(var(--chart-1))",
  },
  {
    label: "Hyp Crisis",
    systolicMin: 180,
    diastolicMin: 120,
    color: "oklch(var(--destructive))",
  },
];

interface BloodPressureChartProps {
  data: BloodPressureResponse | null;
  selectedPeriod: TimePeriod;
  currentDate: Date;
  systolicYAxis: { min: number; max: number };
  diastolicYAxis: { min: number; max: number };
  isSystolic: boolean;
}

export const BloodPressureChart = ({
  data,
  selectedPeriod,
  currentDate,
  systolicYAxis,
  diastolicYAxis,
  isSystolic,
}: BloodPressureChartProps) => {
  // Handle empty data by creating empty chart data
  const hasData =
    data &&
    data.systolic &&
    data.diastolic &&
    (data.systolic.data.length > 0 || data.diastolic.data.length > 0);
  // Calculate Y-axis interval based on range
  const calculateYAxisInterval = (min: number, max: number): number => {
    const range = max - min;
    // Target roughly 4-6 ticks
    if (range <= 10) return 2;
    if (range <= 20) return 5;
    if (range <= 50) return 10;
    if (range <= 100) return 20;
    if (range <= 200) return 50;
    if (range <= 500) return 100;
    return 200;
  };

  // Get Y-axis ticks based on calculated interval
  const getYAxisTicks = (min: number, max: number): number[] => {
    const interval = calculateYAxisInterval(min, max);
    const ticks: number[] = [];

    // Start from the next integer >= min
    let tick = Math.ceil(min);
    while (tick <= Math.floor(max)) {
      ticks.push(tick);
      tick += interval;
    }
    // Ensure we include the exact floor(max) as the last tick
    const last = Math.floor(max);
    if (ticks.length === 0 || ticks[ticks.length - 1] !== last) {
      ticks.push(last);
    }
    return ticks;
  };

  // BP Day view ticks: fixed 10 interval, start at ceil(min), end at floor(max)
  const getBloodPressureDayTicks = (min: number, max: number): number[] => {
    const interval = 10;
    const ticks: number[] = [];
    let tick = Math.ceil(min);
    while (tick <= Math.floor(max)) {
      ticks.push(tick);
      tick += interval;
    }
    const last = Math.floor(max);
    if (ticks.length === 0 || ticks[ticks.length - 1] !== last) {
      ticks.push(last);
    }
    return ticks;
  };

  // Get blood pressure category color for a given systolic and diastolic value
  const getBloodPressureCategoryColor = (
    systolic: number,
    diastolic: number
  ): string => {
    for (const category of BLOOD_PRESSURE_CATEGORIES) {
      let matches = true;

      if (category.systolicMin !== undefined && systolic < category.systolicMin)
        matches = false;
      if (category.systolicMax !== undefined && systolic > category.systolicMax)
        matches = false;
      if (
        category.diastolicMin !== undefined &&
        diastolic < category.diastolicMin
      )
        matches = false;
      if (
        category.diastolicMax !== undefined &&
        diastolic > category.diastolicMax
      )
        matches = false;

      if (matches) return category.color;
    }

    return "oklch(var(--success))"; // Default to Normal color
  };

  // Determine BP category with priority like Flutter (later in list = higher priority)
  const determineBloodPressureCategory = (
    systolic: number,
    diastolic: number
  ): { label: string; color: string } => {
    if (systolic === 0 && diastolic === 0) {
      return { label: "Normal", color: "oklch(var(--muted-foreground))" }; // muted fallback
    }

    let systolicCategory: { label: string; color: string } | null = null;
    let diastolicCategory: { label: string; color: string } | null = null;

    if (systolic > 0) {
      for (const range of BLOOD_PRESSURE_CATEGORIES) {
        const inRange =
          (range.systolicMin === undefined || systolic >= range.systolicMin) &&
          (range.systolicMax === undefined || systolic <= range.systolicMax);
        if (inRange) {
          systolicCategory = { label: range.label, color: range.color };
          break;
        }
      }
    }

    if (diastolic > 0) {
      for (const range of BLOOD_PRESSURE_CATEGORIES) {
        const inRange =
          (range.diastolicMin === undefined ||
            diastolic >= range.diastolicMin) &&
          (range.diastolicMax === undefined || diastolic <= range.diastolicMax);
        if (inRange) {
          diastolicCategory = { label: range.label, color: range.color };
          break;
        }
      }
    }

    if (systolicCategory && diastolicCategory) {
      const sIdx = BLOOD_PRESSURE_CATEGORIES.findIndex(
        (r) => r.label === systolicCategory!.label
      );
      const dIdx = BLOOD_PRESSURE_CATEGORIES.findIndex(
        (r) => r.label === diastolicCategory!.label
      );
      return sIdx >= dIdx ? systolicCategory : diastolicCategory;
    }
    if (systolicCategory) return systolicCategory;
    if (diastolicCategory) return diastolicCategory;
    return { label: "Normal", color: "oklch(var(--muted-foreground))" };
  };

  // Transform blood pressure data for chart display
  const transformBloodPressureDataForChart = () => {
    if (!data) return [];

    // For day view, we need both systolic and diastolic data to show two lines
    // For week/month/year views, we only need the currently selected type
    if (selectedPeriod === "day") {
      // Combine both systolic and diastolic data
      const systolicData = data.systolic.data.map((reading) => {
        let d: Date;
        try {
          d =
            reading.time.includes("AM") || reading.time.includes("PM")
              ? new Date(`${reading.date} ${reading.time}`)
              : new Date(`${reading.date}T${reading.time}`);
          if (isNaN(d.getTime())) d = new Date(reading.date);
        } catch {
          d = new Date(reading.date);
        }

        return {
          xValue: d.getTime(),
          value: reading.value,
          date: reading.date,
          time: reading.time,
          isSystolic: true,
        };
      });

      const diastolicData = data.diastolic.data.map((reading) => {
        let d: Date;
        try {
          d =
            reading.time.includes("AM") || reading.time.includes("PM")
              ? new Date(`${reading.date} ${reading.time}`)
              : new Date(`${reading.date}T${reading.time}`);
          if (isNaN(d.getTime())) d = new Date(reading.date);
        } catch {
          d = new Date(reading.date);
        }

        return {
          xValue: d.getTime(),
          value: reading.value,
          date: reading.date,
          time: reading.time,
          isSystolic: false,
        };
      });

      // Combine and sort by time
      return [...systolicData, ...diastolicData].sort(
        (a, b) => a.xValue - b.xValue
      );
    } else {
      // For week/month/year views, only return the currently selected type
      const source = isSystolic ? data.systolic.data : data.diastolic.data;

      return source
        .map((reading) => {
          let d: Date;
          try {
            d =
              reading.time.includes("AM") || reading.time.includes("PM")
                ? new Date(`${reading.date} ${reading.time}`)
                : new Date(`${reading.date}T${reading.time}`);
            if (isNaN(d.getTime())) d = new Date(reading.date);
          } catch {
            d = new Date(reading.date);
          }

          return {
            xValue: d.getTime(),
            value: reading.value,
            date: reading.date,
            time: reading.time,
            isSystolic,
          };
        })
        .sort((a, b) => a.xValue - b.xValue);
    }
  };

  // Create empty chart data structure for week/month/year views when no data
  const createEmptyChartData = () => {
    if (selectedPeriod === "day") return [];

    let allCategories: string[] = [];

    // Generate complete range for each period
    if (selectedPeriod === "week") {
      // Always show all 7 days of the week
      allCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    } else if (selectedPeriod === "month") {
      // Generate all days of the current month
      const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );
      const daysInMonth = endOfMonth.getDate();

      allCategories = Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString()
      );
    } else if (selectedPeriod === "year") {
      // Always show all 12 months
      allCategories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    }

    // Return empty structure with all categories
    return allCategories.map((key) => ({
      categoryKey: key,
      minValue: null,
      maxValue: null,
      mid: null,
      offsetRange: null,
    }));
  };

  // Create blood pressure range data for week/month/year views
  const createBloodPressureRangeData = () => {
    if (selectedPeriod === "day" || !hasData) return [];

    let allCategories: string[] = [];

    // Generate complete range for each period
    if (selectedPeriod === "week") {
      // Always show all 7 days of the week
      allCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    } else if (selectedPeriod === "month") {
      // Generate all days of the current month
      const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );
      const daysInMonth = endOfMonth.getDate();

      allCategories = Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString()
      );
    } else if (selectedPeriod === "year") {
      // Always show all 12 months
      allCategories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    }

    // Build range data using raw API data for the currently toggled series
    const source = isSystolic ? data.systolic.data : data.diastolic.data;

    // Helper to compute category key for a reading
    const getCategoryKey = (dateStr: string, timeStr: string) => {
      let d: Date;
      try {
        d =
          timeStr.includes("AM") || timeStr.includes("PM")
            ? new Date(`${dateStr} ${timeStr}`)
            : new Date(`${dateStr}T${timeStr}`);
        if (isNaN(d.getTime())) d = new Date(dateStr);
      } catch {
        d = new Date(dateStr);
      }
      if (selectedPeriod === "week")
        return d.toLocaleDateString("en-US", { weekday: "short" });
      if (selectedPeriod === "month") return d.getDate().toString();
      if (selectedPeriod === "year")
        return d.toLocaleDateString("en-US", { month: "short" });
      return "";
    };

    // Aggregate min/max per category
    const map: Record<string, { min: number; max: number }> = {};
    allCategories.forEach((c) => {
      map[c] = { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY };
    });
    source.forEach((r) => {
      const key = getCategoryKey(r.date, r.time);
      if (!key || !(key in map)) return;
      map[key].min = Math.min(map[key].min, r.value);
      map[key].max = Math.max(map[key].max, r.value);
    });

    const rangeData = allCategories.map((key) => {
      const { min, max } = map[key];
      if (!isFinite(min) || !isFinite(max)) {
        return {
          categoryKey: key,
          minValue: null as any,
          maxValue: null as any,
          mid: null as any,
          offsetRange: null as any,
        };
      }
      const mid = (min + max) / 2;
      // ErrorBar expects positive offsets relative to the y value (mid): [mid-min, max-mid]
      const offsetRange: [number, number] = [mid - min, max - mid];
      return {
        categoryKey: key,
        minValue: min,
        maxValue: max,
        mid,
        offsetRange,
      };
    });

    return rangeData;
  };

  const chartData = hasData ? transformBloodPressureDataForChart() : [];

  if (selectedPeriod === "day") {
    // Day view: Use LineChart with two lines (systolic and diastolic)
    const dayXAxis = getDayXAxis(currentDate);

    // When no data, create placeholder points so axes still render
    const dayChartData = hasData
      ? chartData
      : dayXAxis.ticks.map((tick: number) => ({
          xValue: tick,
          value: null,
        }));

    return (
      <ChartContainer className="w-full h-full" config={{}}>
        <LineChart
          data={dayChartData}
          margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
        >
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray="4 4"
            horizontalCoordinatesGenerator={(props: any) => {
              const yAxis =
                (props && props.yAxis && (props.yAxis[0] || props.yAxis)) ||
                null;
              const scale = yAxis && yAxis.scale ? yAxis.scale : null;
              const ticks = getBloodPressureDayTicks(
                diastolicYAxis.min,
                systolicYAxis.max
              );
              return scale ? ticks.map((t: number) => (scale as any)(t)) : [];
            }}
          />
          <XAxis
            type="number"
            dataKey="xValue"
            scale="time"
            domain={dayXAxis.domain}
            axisLine={{
              stroke: CHART_COLORS.axis,
              strokeWidth: 1,
            }}
            tickLine={{
              stroke: CHART_COLORS.axis,
              strokeWidth: 1.5,
            }}
            tick={{ fontSize: 13, fill: CHART_COLORS.tick }}
            height={35}
            ticks={dayXAxis.ticks}
            tickFormatter={dayXAxis.tickFormatter}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 13, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={[
              Math.floor(diastolicYAxis.min),
              Math.floor(systolicYAxis.max),
            ]}
            ticks={getBloodPressureDayTicks(
              diastolicYAxis.min,
              systolicYAxis.max
            )}
            interval={0}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const point = payload[0].payload as any;
                // Find pair at same date/time
                const systolicPoint = (chartData as any[]).find(
                  (p: any) =>
                    p.isSystolic &&
                    p.date === point.date &&
                    p.time === point.time
                );
                const diastolicPoint = (chartData as any[]).find(
                  (p: any) =>
                    !p.isSystolic &&
                    p.date === point.date &&
                    p.time === point.time
                );
                const systolicVal = systolicPoint
                  ? systolicPoint.value
                  : undefined;
                const diastolicVal = diastolicPoint
                  ? diastolicPoint.value
                  : undefined;
                const combined =
                  systolicVal !== undefined && diastolicVal !== undefined
                    ? `${systolicVal}-${diastolicVal}`
                    : `${point.value}`;
                return (
                  <div className="bg-foreground text-white text-xs rounded-md p-2 shadow-xl">
                    <p className="font-medium text-muted-foreground">
                      Blood Pressure
                    </p>
                    <p className="text-sm font-bold text-white">
                      {combined} mmHg
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {point.time}
                    </p>
                  </div>
                );
              }
              return null;
            }}
            cursor={{ stroke: "oklch(var(--border))", strokeWidth: 2 }}
          />
          {/* Only render lines if we have data */}
          {hasData && (
            <>
              {/* Systolic line */}
              <Line
                type="monotone"
                dataKey="value"
                data={chartData.filter((item: any) => item.isSystolic)}
                stroke={BLOOD_PRESSURE_COLORS.systolic}
                strokeWidth={2}
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  const pair = (chartData as any[]).find(
                    (p: any) =>
                      !p.isSystolic &&
                      p.date === payload.date &&
                      p.time === payload.time
                  );
                  const category = determineBloodPressureCategory(
                    payload.value,
                    pair ? pair.value : 0
                  );
                  const color = category.color;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={6}
                      fill={color}
                      strokeWidth={0}
                    />
                  );
                }}
                activeDot={{
                  r: 8,
                  strokeWidth: 0,
                  fill: BLOOD_PRESSURE_COLORS.systolic,
                }}
                connectNulls={false}
              />
              {/* Diastolic line */}
              <Line
                type="monotone"
                dataKey="value"
                data={chartData.filter((item: any) => !item.isSystolic)}
                stroke={BLOOD_PRESSURE_COLORS.diastolic}
                strokeWidth={2}
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  const pair = (chartData as any[]).find(
                    (p: any) =>
                      p.isSystolic &&
                      p.date === payload.date &&
                      p.time === payload.time
                  );
                  const category = determineBloodPressureCategory(
                    pair ? pair.value : 0,
                    payload.value
                  );
                  const color = category.color;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={6}
                      fill={color}
                      strokeWidth={0}
                    />
                  );
                }}
                activeDot={{
                  r: 8,
                  strokeWidth: 0,
                  fill: BLOOD_PRESSURE_COLORS.diastolic,
                }}
                connectNulls={false}
              />
            </>
          )}
        </LineChart>
      </ChartContainer>
    );
  } else {
    // Week/Month/Year views: show two points (min & max) with a vertical connector line
    const weekMonthYearData = hasData
      ? createBloodPressureRangeData()
      : createEmptyChartData();

    return (
      <ChartContainer className="w-full h-full" config={{}}>
        <LineChart
          data={weekMonthYearData}
          margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
        >
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray="4 4"
            horizontalCoordinatesGenerator={(props: any) => {
              const yAxis =
                (props && props.yAxis && (props.yAxis[0] || props.yAxis)) ||
                null;
              const scale = yAxis && yAxis.scale ? yAxis.scale : null;
              const ticks = isSystolic
                ? getYAxisTicks(systolicYAxis.min, systolicYAxis.max)
                : getYAxisTicks(diastolicYAxis.min, diastolicYAxis.max);
              return scale ? ticks.map((t: number) => (scale as any)(t)) : [];
            }}
          />
          <XAxis
            dataKey="categoryKey"
            axisLine={{
              stroke: CHART_COLORS.axis,
              strokeWidth: 1,
            }}
            tickLine={{
              stroke: CHART_COLORS.axis,
              strokeWidth: 1.5,
            }}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            height={35}
            interval={0}
            tickFormatter={getXAxisTickFormatterForPeriod(
              selectedPeriod,
              currentDate
            )}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={
              isSystolic
                ? [systolicYAxis.min, systolicYAxis.max]
                : [diastolicYAxis.min, diastolicYAxis.max]
            }
            ticks={
              isSystolic
                ? getYAxisTicks(systolicYAxis.min, systolicYAxis.max)
                : getYAxisTicks(diastolicYAxis.min, diastolicYAxis.max)
            }
            interval={0}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                if (data.minValue !== null && data.maxValue !== null) {
                  return (
                    <div className="bg-foreground text-white text-xs rounded-md p-2 shadow-xl">
                      <p className="font-medium text-muted-foreground">
                        {isSystolic ? "Systolic" : "Diastolic"}
                      </p>
                      <p className="text-sm font-bold text-white">
                        {data.minValue === data.maxValue
                          ? data.minValue
                          : `${data.minValue}-${data.maxValue}`}{" "}
                        mmHg
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {label}
                      </p>
                    </div>
                  );
                }
              }
              return null;
            }}
            cursor={{ stroke: "oklch(var(--border))", strokeWidth: 1 }}
          />
          {/* Only render chart elements if we have data */}
          {hasData && (
            <>
              {/* Mid point is invisible; ErrorBar renders the vertical connector between min and max */}
              <Line
                type="monotone"
                dataKey="mid"
                stroke="transparent"
                strokeWidth={0}
                dot={false}
                activeDot={false}
                connectNulls
              >
                {/* ErrorBar expects [neg, pos] offsets relative to y */}
                <ErrorBar
                  dataKey="offsetRange"
                  width={0}
                  stroke={
                    isSystolic
                      ? BLOOD_PRESSURE_COLORS.systolic
                      : BLOOD_PRESSURE_COLORS.diastolic
                  }
                  strokeWidth={6}
                  strokeOpacity={0.35}
                  direction="y"
                />
              </Line>
              {/* Render explicit dots at min and max values without connecting lines */}
              <Line
                type="linear"
                dataKey="minValue"
                stroke="transparent"
                strokeWidth={0}
                dot={{
                  r: 6,
                  strokeWidth: 0,
                  fill: isSystolic
                    ? BLOOD_PRESSURE_COLORS.systolic
                    : BLOOD_PRESSURE_COLORS.diastolic,
                }}
                activeDot={{
                  r: 8,
                  strokeWidth: 0,
                  fill: isSystolic
                    ? BLOOD_PRESSURE_COLORS.systolic
                    : BLOOD_PRESSURE_COLORS.diastolic,
                }}
                connectNulls
              />
              <Line
                type="linear"
                dataKey="maxValue"
                stroke="transparent"
                strokeWidth={0}
                dot={{
                  r: 6,
                  strokeWidth: 0,
                  fill: isSystolic
                    ? BLOOD_PRESSURE_COLORS.systolic
                    : BLOOD_PRESSURE_COLORS.diastolic,
                }}
                activeDot={{
                  r: 8,
                  strokeWidth: 0,
                  fill: isSystolic
                    ? BLOOD_PRESSURE_COLORS.systolic
                    : BLOOD_PRESSURE_COLORS.diastolic,
                }}
                connectNulls
              />
            </>
          )}
        </LineChart>
      </ChartContainer>
    );
  }
};

export { BLOOD_PRESSURE_COLORS, BLOOD_PRESSURE_CATEGORIES };
